import React from "react";
import ReactDOM from "react-dom";

import { authToken, authUser } from "../server";
import {
  adminUploaderMessage,
  isAdminUploaderMessage,
} from "./adminUploaderProtocol";

const { REACT_APP_UPLOADER } = process.env;

const styles = {
  iframeBg: {
    position: "fixed",
    top: 68,
    right: 40,
    width: 470,
    bottom: 0,
    zIndex: 999,
    backgroundColor: "white",
    textAlign: "right",
  },
  iframe: {
    border: "1px solid rgba(0, 0, 0, 0.42)",
    borderRadius: 3,
    borderRight: "none",
    padding: 4,
    paddingBottom: 30,
    backgroundColor: "white",
  },
};

function editingLecture(hash) {
  const matches = (hash || "").match(/^#\/lectures\/([0-9]+|create)/);
  if (!matches) return -1;
  return matches[1] === "create" ? 0 : parseInt(matches[1], 10);
}

function lectureHasMedia(record = {}) {
  // These are the canonical media fields displayed on the lecture editor.
  return Boolean(
    record.mp4_url ||
      record.m3u8_url ||
      record.watermarked_original_mp4_url ||
      record.mp3_url,
  );
}

function currentLectureContext() {
  return {
    lectureId: editingLecture(window.location.hash),
    hasMedia: lectureHasMedia(_lectureActions?.record),
  };
}

export const MediaUploadContext = React.createContext({});

/** Panel that remains mounted so uploads continue while it is hidden. */
class MediaUploadPanel extends React.PureComponent {
  static contextType = MediaUploadContext;

  render() {
    const { iframeSrc, refIframe, showing } = this.context;
    return (
      <div
        style={{
          ...styles.iframeBg,
          visibility: showing ? "visible" : "hidden",
        }}
      >
        <iframe
          ref={refIframe}
          id="uploader"
          src={iframeSrc}
          title="Media Uploader"
          frameBorder="0"
          scrolling="yes"
          width="100%"
          height="100%"
          style={{
            ...styles.iframe,
            visibility: showing ? "visible" : "hidden",
          }}
        />
      </div>
    );
  }
}

const MediaUploadPortal = ReactDOM.createPortal(
  <MediaUploadPanel />,
  document.getElementById("uploader-root"),
);

/** Hosts the website uploader and coordinates lecture saves with its iframe. */
export class MediaUploadProvider extends React.PureComponent {
  state = {
    activeUploadCount: 0,
    allowed: this.props.allowed,
    iframe: null,
    iframeSrc: REACT_APP_UPLOADER,
    showing: false,
    refIframe: (iframe) => this.setState({ iframe }),
    toggle: () =>
      this.setState((state) => ({
        showing: !state.showing,
      })),
  };

  iframeReady = false;
  pendingSaveRequestId = undefined;
  unsubscribeLogout = undefined;
  unsubscribeToken = undefined;

  get uploaderOrigin() {
    return new URL(REACT_APP_UPLOADER).origin;
  }

  postMessage = (type, data = {}) => {
    const { iframe } = this.state;
    if (!iframe?.contentWindow) return;
    iframe.contentWindow.postMessage(
      adminUploaderMessage(type, data),
      this.uploaderOrigin,
    );
  };

  sendAuthentication = (type = "authenticate") => {
    if (!this.iframeReady) return;
    const token = authToken();
    const expiration = localStorage.getItem("jwtExpiry");
    if (!token || !expiration || !authUser.deviceId) return;
    this.postMessage(type, {
      token,
      expiration,
      deviceId: authUser.deviceId,
      ...(type === "initialize" ? { lecture: currentLectureContext() } : {}),
    });
  };

  sendLectureContext = () => {
    if (!this.iframeReady) return;
    this.postMessage("lecture-context", {
      lecture: currentLectureContext(),
    });
  };

  completeSave = (ok, lectureId, error) => {
    if (!this.pendingSaveRequestId) return;
    const requestId = this.pendingSaveRequestId;
    this.pendingSaveRequestId = undefined;
    this.postMessage("save-result", { requestId, ok, lectureId, error });
  };

  requestLectureSave = async (requestId) => {
    if (this.pendingSaveRequestId) {
      this.postMessage("save-result", {
        requestId,
        ok: false,
        error: "A lecture save is already in progress.",
      });
      return;
    }

    const lectureId = editingLecture(window.location.hash);
    if (lectureId < 0 || !_lectureActions) {
      this.postMessage("save-result", {
        requestId,
        ok: false,
        error: "Open a lecture in Admin before attaching media.",
      });
      return;
    }

    if (lectureId > 0 && _lectureActions.pristine) {
      this.postMessage("save-result", { requestId, ok: true, lectureId });
      return;
    }

    if (_lectureActions.pristine) {
      _lectureActions.showNotification("ra.message.invalid_form", {
        type: "warning",
      });
      this.postMessage("save-result", {
        requestId,
        ok: false,
        error: "Complete the new lecture before attaching media.",
      });
      return;
    }

    this.pendingSaveRequestId = requestId;
    try {
      const submitted = await _lectureActions.submit();
      if (!submitted) {
        _lectureActions.showNotification("ra.message.invalid_form", {
          type: "warning",
        });
        this.completeSave(false, undefined, "The lecture form is invalid.");
      }
    } catch (error) {
      this.completeSave(
        false,
        undefined,
        error instanceof Error
          ? error.message
          : "The lecture could not be saved.",
      );
    }
  };

  onWindowMessage = (event) => {
    if (
      event.origin !== this.uploaderOrigin ||
      event.source !== this.state.iframe?.contentWindow ||
      !isAdminUploaderMessage(event.data)
    ) {
      return;
    }

    switch (event.data.type) {
      case "ready":
        this.iframeReady = true;
        this.sendAuthentication("initialize");
        break;
      case "active-upload-count":
        this.setState({ activeUploadCount: event.data.count || 0 });
        break;
      case "save-request":
        if (typeof event.data.requestId === "string") {
          void this.requestLectureSave(event.data.requestId);
        }
        break;
      default:
        break;
    }
  };

  onHashChange = () => {
    this.sendLectureContext();
    const lectureId = editingLecture(window.location.hash);
    if (lectureId > -1 && !authUser.isAdministrator && authUser.isAuthor) {
      MediaUploader.show();
    }
  };

  onLogout = () => {
    this.postMessage("clear-authentication");
    this.setState({ showing: false });
  };

  componentDidMount() {
    _provider = this;
    window.addEventListener("message", this.onWindowMessage);
    window.addEventListener("hashchange", this.onHashChange);
    this.unsubscribeLogout = authUser.onLogout(this.onLogout);
    this.unsubscribeToken = authUser.onToken(this.sendAuthentication);
  }

  componentWillUnmount() {
    _provider = undefined;
    window.removeEventListener("message", this.onWindowMessage);
    window.removeEventListener("hashchange", this.onHashChange);
    this.unsubscribeLogout?.();
    this.unsubscribeToken?.();
  }

  render() {
    const { allowed, children } = this.props;
    if (!allowed) return children;
    return (
      <MediaUploadContext.Provider value={this.state}>
        {MediaUploadPortal}
        {children}
      </MediaUploadContext.Provider>
    );
  }
}

let _provider;
let _lectureActions;

/** Out-of-context API used by the lecture forms and uploader toolbar button. */
export const MediaUploader = {
  setLectureActions(actions) {
    _lectureActions = actions;
    _provider?.sendLectureContext();
  },
  lectureSaveSucceeded(lectureId) {
    _provider?.completeSave(true, lectureId);
  },
  lectureSaveFailed(error) {
    _provider?.completeSave(
      false,
      undefined,
      error?.message || "The lecture could not be saved.",
    );
  },
  show(showing = true) {
    _provider?.setState({ showing });
  },
};
